const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export interface AttendanceLastImportLike {
  lastImportAt?: string;
  lastImportMonth?: number;
  lastImportYear?: number;
  lastImportEndMonth?: number;
  lastImportEndYear?: number;
  lastImportPeriodLabel?: string;
}

export interface AttendanceImportPeriodLike {
  periodLabel?: string;
  startMonth?: number;
  startYear?: number;
  endMonth?: number;
  endYear?: number;
}

export const formatAttendanceImportPeriod = (
  period: AttendanceImportPeriodLike | null | undefined
) => {
  if (!period) return '';

  if (period.periodLabel) return period.periodLabel;

  const { startMonth, startYear, endMonth, endYear } = period;
  if (!startMonth || !startYear) return '';

  const startLabel = `${MONTH_NAMES[startMonth - 1]} ${startYear}`;
  if (!endMonth || !endYear || (endMonth === startMonth && endYear === startYear)) {
    return startLabel;
  }

  return `${startLabel} – ${MONTH_NAMES[endMonth - 1]} ${endYear}`;
};

export const formatAttendanceLastImport = (
  lastImport: AttendanceLastImportLike | null | undefined
) => {
  if (!lastImport?.lastImportAt) return 'No imports yet';

  const period = formatAttendanceImportPeriod({
    periodLabel: lastImport.lastImportPeriodLabel,
    startMonth: lastImport.lastImportMonth,
    startYear: lastImport.lastImportYear,
    endMonth: lastImport.lastImportEndMonth,
    endYear: lastImport.lastImportEndYear
  });
  const date = parseApiUtcDate(lastImport.lastImportAt).toLocaleString();

  return period ? `${period} · ${date}` : date;
};

export const formatAttendanceLastImportDate = (
  lastImport: AttendanceLastImportLike | null | undefined
) => {
  if (!lastImport?.lastImportAt) return 'No imports yet';

  const date = parseApiUtcDate(lastImport.lastImportAt);
  const day = date.getDate();
  const month = MONTH_NAMES[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

export const parseApiUtcDate = (value: string): Date => {
  const trimmed = value.trim();
  if (!trimmed) return new Date(NaN);

  if (/[zZ]$|[+-]\d{2}:\d{2}$/.test(trimmed)) {
    return new Date(trimmed);
  }

  return new Date(`${trimmed}Z`);
};

export const formatAttendanceLocalDateTime = (iso?: string | null) => {
  if (!iso) return null;

  const date = parseApiUtcDate(iso);
  if (Number.isNaN(date.getTime())) return iso;

  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
};

export const formatAttendanceSavedAt = (iso?: string) => {
  if (!iso) return null;

  const date = parseApiUtcDate(iso);
  const day = date.getDate();
  const month = MONTH_NAMES[date.getMonth()];
  const year = date.getFullYear();
  const time = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  });

  return `${day} ${month} ${year} at ${time}`;
};

export const formatSavedByLabel = (upn?: string) => {
  if (!upn) return '';

  const localPart = upn.split('@')[0] ?? upn;
  return localPart
    .split('.')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};
